import type { CountryCode } from "libphonenumber-js"

import { useId } from "react"

import styles from "./index.module.css"
import { BaseField, IconWrapper } from "./variant/baseField"
import { EmailField } from "./variant/emailField"
import { PasswordField } from "./variant/passwordField"
import { PhoneField } from "./variant/phoneField"

interface BaseProps extends Omit<React.ComponentPropsWithRef<"input">, "prefix" | "suffix"> {
  label?: string
  iconLeft?: React.ReactNode
  iconRight?: React.ReactNode
  errorText?: string
}

export type TextFieldProps = BaseProps &
  (
    | {
        type: Extract<BaseProps["type"], "tel">
        countryCodes?: CountryCode[] | (string[] & {})
        defaultCountryCode?: CountryCode | (string & {})
      }
    | {
        countryCodes?: never
        defaultCountryCode?: never
      }
  )

export const TextField = ({ id: providedId, errorText, label, iconLeft, iconRight, ...inputProps }: TextFieldProps) => {
  const generatedId = useId()
  const id = providedId ?? generatedId

  if (inputProps.type === "email") {
    return <EmailField id={id} errorText={errorText} label={label} iconLeft={iconLeft} {...inputProps} />
  }
  if (inputProps.type === "tel") {
    return <PhoneField id={id} errorText={errorText} label={label} {...inputProps} />
  }
  if (inputProps.type === "password") {
    return <PasswordField id={id} errorText={errorText} label={label} iconLeft={iconLeft} {...inputProps} />
  }

  return (
    <BaseField
      label={label}
      errorText={errorText}
      required={inputProps.required}
      disabled={inputProps.disabled}
      id={id}
    >
      {iconLeft && <IconWrapper position="left">{iconLeft}</IconWrapper>}
      <input {...inputProps} id={id} className={styles.input} />
      {iconRight && <IconWrapper position="right">{iconRight}</IconWrapper>}
    </BaseField>
  )
}
